import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const ENGRAVING_ADDON_DEFAULT_HANDLE = 'engraving';
const ENGRAVING_ADDON_FALLBACK_HANDLES = [
  ENGRAVING_ADDON_DEFAULT_HANDLE,
  'engraving-upcharge',
  'engraving-upcharge-1',
  'engraving-addon',
];
const ENGRAVING_ADDON_CACHE_KEY = '__engraving-addon-variant-id__';
const engravingAddonVariantCache = new Map();
let engravingGuardSyncInFlight = false;
const LINKED_ADDON_CONFIGS = [
  {
    type: 'custom_charm_selection',
    handles: ['custom-charm-selection'],
    title: 'custom charm selection',
    mainLineProperty: 'Custom Charm Selection',
    variantIdProperty: '_custom_charm_selection_addon_variant_id',
  },
  {
    type: 'natural_stone_beads',
    handles: ['beads', 'natural-stone-beads'],
    title: 'natural stone beads',
    mainLineProperty: 'Natural Stone Beads',
    variantIdProperty: '_natural_stone_beads_addon_variant_id',
  },
];
const linkedAddonVariantCache = new Map();
let linkedAddonGuardSyncInFlight = false;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
        void this.#ensureEngravingAddonIntegrity(parsedResponseText);
        void this.#ensureLinkedAddonIntegrity(parsedResponseText);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    void this.#ensureEngravingAddonIntegrity(event.detail?.resource);
    void this.#ensureLinkedAddonIntegrity(event.detail?.resource);
  };

  /**
   * Reads a line item property value safely.
   * @param {any} item
   * @param {string} key
   * @returns {string}
   */
  #lineItemProperty(item, key) {
    const properties = item?.properties;
    if (!properties || typeof properties !== 'object') return '';
    const value = properties[key];
    if (value == null) return '';
    return `${value}`.trim();
  }

  /**
   * Whether cart line is an engraving add-on line.
   * @param {any} item
   * @returns {boolean}
   */
  #isEngravingAddonLine(item) {
    return this.#lineItemProperty(item, '_engraving_addon').toLowerCase() === 'true';
  }

  /**
   * Whether cart line is an engraved main product line.
   * @param {any} item
   * @returns {boolean}
   */
  #isEngravedMainLine(item) {
    if (this.#isEngravingAddonLine(item)) return false;

    const engravingText = this.#lineItemProperty(item, 'Engraving text');
    const engravingOptIn = this.#lineItemProperty(item, 'Engraving add-on');
    return Boolean(engravingText || engravingOptIn);
  }

  /**
   * Whether cart line is a linked add-on line.
   * @param {any} item
   * @param {string} [addonType]
   * @returns {boolean}
   */
  #isLinkedAddonLine(item, addonType = '') {
    if (this.#lineItemProperty(item, '_linked_addon').toLowerCase() !== 'true') return false;
    if (!addonType) return true;
    return this.#lineItemProperty(item, '_linked_addon_type') === addonType;
  }

  /**
   * Whether a main product line has a selected linked add-on.
   * @param {any} item
   * @param {{ mainLineProperty: string }} config
   * @returns {boolean}
   */
  #isLinkedAddonMainLine(item, config) {
    if (this.#isEngravingAddonLine(item) || this.#isLinkedAddonLine(item)) return false;
    return this.#linkedAddonUnitsForMainLine(item, config) > 0;
  }

  /**
   * Number of add-on units selected on a main line item.
   * @param {any} item
   * @param {{ mainLineProperty: string }} config
   * @returns {number}
   */
  #linkedAddonUnitsForMainLine(item, config) {
    return Math.max(0, Number(this.#lineItemProperty(item, config.mainLineProperty)) || 0);
  }

  /**
   * Resolves linked add-on variant ID.
   * @param {any} cartResource
   * @param {{ type: string, handles: string[], title: string, variantIdProperty: string, mainLineProperty: string }} config
   * @returns {Promise<number | null>}
   */
  async #resolveLinkedAddonVariantId(cartResource, config) {
    const existingAddonLine = cartResource?.items?.find((item) => this.#isLinkedAddonLine(item, config.type));
    const existingAddonVariantId = Number(existingAddonLine?.variant_id || existingAddonLine?.id || '');
    if (Number.isFinite(existingAddonVariantId) && existingAddonVariantId > 0) {
      return existingAddonVariantId;
    }

    const variantIdFromMainLine = Number(
      cartResource?.items?.find((item) => this.#isLinkedAddonMainLine(item, config))?.properties?.[config.variantIdProperty] || ''
    );
    if (Number.isFinite(variantIdFromMainLine) && variantIdFromMainLine > 0) {
      return variantIdFromMainLine;
    }

    for (const addonHandle of config.handles) {
      const cacheKey = `handle:${addonHandle}`;
      if (linkedAddonVariantCache.has(cacheKey)) {
        const cachedId = linkedAddonVariantCache.get(cacheKey);
        if (cachedId) return cachedId;
        continue;
      }

      try {
        const response = await fetch(`/products/${encodeURIComponent(addonHandle)}.js`);
        if (!response.ok) {
          linkedAddonVariantCache.set(cacheKey, null);
          continue;
        }

        const addonProduct = await response.json();
        const addonVariant = addonProduct?.variants?.find((variant) => variant?.available) ?? addonProduct?.variants?.[0];
        const variantId = Number(addonVariant?.id || '');
        const resolvedId = Number.isFinite(variantId) && variantId > 0 ? variantId : null;
        linkedAddonVariantCache.set(cacheKey, resolvedId);
        if (resolvedId) return resolvedId;
      } catch (error) {
        console.error('Failed to resolve linked add-on in cart guard:', config.type, error);
        linkedAddonVariantCache.set(cacheKey, null);
      }
    }

    const titleCacheKey = `title:${config.title}`;
    if (linkedAddonVariantCache.has(titleCacheKey)) {
      return linkedAddonVariantCache.get(titleCacheKey);
    }

    try {
      const response = await fetch('/products.json?limit=250');
      if (!response.ok) {
        linkedAddonVariantCache.set(titleCacheKey, null);
        return null;
      }

      const data = await response.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      const addonProduct = products.find((product) => `${product?.title || ''}`.trim().toLowerCase() === config.title);
      const addonVariant = addonProduct?.variants?.find((variant) => variant?.available) ?? addonProduct?.variants?.[0];
      const addonVariantId = Number(addonVariant?.id || '');
      const resolvedId = Number.isFinite(addonVariantId) && addonVariantId > 0 ? addonVariantId : null;

      linkedAddonVariantCache.set(titleCacheKey, resolvedId);
      if (addonProduct?.handle) {
        linkedAddonVariantCache.set(`handle:${`${addonProduct.handle}`.trim().toLowerCase()}`, resolvedId);
      }

      return resolvedId;
    } catch (error) {
      console.error('Failed to resolve linked add-on via product lookup in cart guard:', config.type, error);
      linkedAddonVariantCache.set(titleCacheKey, null);
      return null;
    }
  }

  /**
   * Resolves engraving add-on variant ID.
   * @param {any} cartResource
   * @returns {Promise<number | null>}
   */
  async #resolveEngravingAddonVariantId(cartResource) {
    const existingAddonLine = cartResource?.items?.find((item) => this.#isEngravingAddonLine(item));
    const existingAddonVariantId = Number(existingAddonLine?.variant_id || existingAddonLine?.id || '');
    if (Number.isFinite(existingAddonVariantId) && existingAddonVariantId > 0) {
      return existingAddonVariantId;
    }

    const variantIdFromEngravedItem = Number(
      cartResource?.items?.find((item) => this.#isEngravedMainLine(item))?.properties?._engraving_addon_variant_id || ''
    );
    if (Number.isFinite(variantIdFromEngravedItem) && variantIdFromEngravedItem > 0) {
      return variantIdFromEngravedItem;
    }

    for (const addonHandle of ENGRAVING_ADDON_FALLBACK_HANDLES) {
      if (engravingAddonVariantCache.has(addonHandle)) {
        const cachedId = engravingAddonVariantCache.get(addonHandle);
        if (cachedId) return cachedId;
        continue;
      }

      try {
        const response = await fetch(`/products/${encodeURIComponent(addonHandle)}.js`);
        if (!response.ok) {
          engravingAddonVariantCache.set(addonHandle, null);
          continue;
        }

        const addonProduct = await response.json();
        const addonVariant = addonProduct?.variants?.find((variant) => variant?.available) ?? addonProduct?.variants?.[0];
        const variantId = Number(addonVariant?.id || '');
        const resolvedId = Number.isFinite(variantId) && variantId > 0 ? variantId : null;
        engravingAddonVariantCache.set(addonHandle, resolvedId);
        if (resolvedId) return resolvedId;
      } catch (error) {
        console.error('Failed to resolve engraving add-on in cart guard:', error);
        engravingAddonVariantCache.set(addonHandle, null);
      }
    }

    if (engravingAddonVariantCache.has(ENGRAVING_ADDON_CACHE_KEY)) {
      return engravingAddonVariantCache.get(ENGRAVING_ADDON_CACHE_KEY);
    }

    try {
      const response = await fetch('/products.json?limit=250');
      if (!response.ok) {
        engravingAddonVariantCache.set(ENGRAVING_ADDON_CACHE_KEY, null);
        return null;
      }

      const data = await response.json();
      const products = Array.isArray(data?.products) ? data.products : [];
      const engravingProduct = products.find((product) => {
        const handle = `${product?.handle || ''}`.toLowerCase();
        const title = `${product?.title || ''}`.toLowerCase();
        return handle.includes('engraving') || title.includes('engraving');
      });

      const addonVariant =
        engravingProduct?.variants?.find((variant) => variant?.available) ?? engravingProduct?.variants?.[0];
      const addonVariantId = Number(addonVariant?.id || '');
      const resolvedId = Number.isFinite(addonVariantId) && addonVariantId > 0 ? addonVariantId : null;

      if (engravingProduct?.handle) {
        engravingAddonVariantCache.set(`${engravingProduct.handle}`.trim().toLowerCase(), resolvedId);
      }
      engravingAddonVariantCache.set(ENGRAVING_ADDON_CACHE_KEY, resolvedId);
      return resolvedId;
    } catch (error) {
      console.error('Failed to resolve engraving add-on via product lookup in cart guard:', error);
      engravingAddonVariantCache.set(ENGRAVING_ADDON_CACHE_KEY, null);
      return null;
    }
  }

  /**
   * Ensures engraved lines in cart always have matching engraving add-on quantities.
   * @param {any} cartResource
   */
  async #ensureEngravingAddonIntegrity(cartResource) {
    if (document.querySelector('cart-items-component') !== this) return;
    if (engravingGuardSyncInFlight) return;

    let cart = cartResource;
    if (!Array.isArray(cart?.items)) {
      try {
        const response = await fetch('/cart.js');
        cart = await response.json();
      } catch (error) {
        console.error('Failed to load cart for engraving guard:', error);
        return;
      }
    }

    const engravedQuantity = cart.items.reduce((total, item) => {
      if (!this.#isEngravedMainLine(item)) return total;
      return total + (Number(item?.quantity) || 0);
    }, 0);

    const addonQuantity = cart.items.reduce((total, item) => {
      if (!this.#isEngravingAddonLine(item)) return total;
      return total + (Number(item?.quantity) || 0);
    }, 0);

    const quantityDelta = engravedQuantity - addonQuantity;
    if (quantityDelta === 0) return;

    engravingGuardSyncInFlight = true;

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sections = Array.from(sectionsToUpdate).join(',');
      if (quantityDelta > 0) {
        const addonVariantId = await this.#resolveEngravingAddonVariantId(cart);
        if (!addonVariantId) return;

        const requestBody = {
          items: [
            {
              id: addonVariantId,
              quantity: quantityDelta,
              properties: {
                _engraving_addon: 'true',
                _engraving_for_variant: 'auto-sync',
              },
            },
          ],
        };

        if (sections) {
          requestBody.sections = sections;
        }

        const fetchCfg = fetchConfig('javascript');
        const response = await fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        const responseData = await response.json();
        if (responseData?.status) return;

        this.dispatchEvent(
          new CartUpdateEvent(responseData, this.sectionId, {
            source: 'engraving-cart-guard',
            sections: responseData?.sections,
          })
        );

        return;
      }

      const addonLines = cart.items
        .map((item) => item)
        .filter((item) => this.#isEngravingAddonLine(item));
      let quantityToRemove = Math.abs(quantityDelta);
      /** @type {Record<string, number>} */
      const updates = {};

      for (const addonLine of addonLines) {
        if (quantityToRemove <= 0) break;

        const currentQuantity = Number(addonLine?.quantity) || 0;
        if (currentQuantity <= 0) continue;

        const removeNow = Math.min(currentQuantity, quantityToRemove);
        const nextQuantity = currentQuantity - removeNow;
        quantityToRemove -= removeNow;

        const lineKey = `${addonLine?.key || ''}`.trim();
        if (lineKey) {
          updates[lineKey] = nextQuantity;
        }
      }

      if (Object.keys(updates).length === 0) return;

      const body = JSON.stringify({
        updates,
        sections,
        sections_url: window.location.pathname,
      });

      const updateResponse = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const updateText = await updateResponse.text();
      const updateData = JSON.parse(updateText);
      if (updateData?.errors) return;

      this.dispatchEvent(
        new CartUpdateEvent(updateData, this.sectionId, {
          source: 'engraving-cart-guard',
          sections: updateData?.sections,
        })
      );
    } catch (error) {
      console.error('Failed to auto-sync engraving add-on:', error);
    } finally {
      engravingGuardSyncInFlight = false;
    }
  }

  /**
   * Ensures checkbox-based linked add-on lines stay in sync with their main products.
   * @param {any} cartResource
   */
  async #ensureLinkedAddonIntegrity(cartResource) {
    if (document.querySelector('cart-items-component') !== this) return;
    if (linkedAddonGuardSyncInFlight) return;

    let cart = cartResource;
    if (!Array.isArray(cart?.items)) {
      try {
        const response = await fetch('/cart.js');
        cart = await response.json();
      } catch (error) {
        console.error('Failed to load cart for linked add-on guard:', error);
        return;
      }
    }

    linkedAddonGuardSyncInFlight = true;

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sections = Array.from(sectionsToUpdate).join(',');

      for (const config of LINKED_ADDON_CONFIGS) {
        const mainQuantity = cart.items.reduce((total, item) => {
          if (!this.#isLinkedAddonMainLine(item, config)) return total;
          const itemQuantity = Number(item?.quantity) || 0;
          const addonUnits = this.#linkedAddonUnitsForMainLine(item, config);
          return total + itemQuantity * addonUnits;
        }, 0);

        const addonQuantity = cart.items.reduce((total, item) => {
          if (!this.#isLinkedAddonLine(item, config.type)) return total;
          return total + (Number(item?.quantity) || 0);
        }, 0);

        const quantityDelta = mainQuantity - addonQuantity;
        if (quantityDelta === 0) continue;

        if (quantityDelta > 0) {
          const addonVariantId = await this.#resolveLinkedAddonVariantId(cart, config);
          if (!addonVariantId) continue;

          const requestBody = {
            items: [
              {
                id: addonVariantId,
                quantity: quantityDelta,
                properties: {
                  _linked_addon: 'true',
                  _linked_addon_type: config.type,
                  _linked_addon_for_variant: 'auto-sync',
                },
              },
            ],
          };

          if (sections) {
            requestBody.sections = sections;
          }

          const fetchCfg = fetchConfig('javascript');
          const response = await fetch(Theme.routes.cart_add_url, {
            ...fetchCfg,
            headers: {
              ...fetchCfg.headers,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });

          const responseData = await response.json();
          if (responseData?.status) continue;

          cart = responseData;
          this.dispatchEvent(
            new CartUpdateEvent(responseData, this.sectionId, {
              source: 'linked-addon-cart-guard',
              sections: responseData?.sections,
            })
          );
          continue;
        }

        const addonLines = cart.items.filter((item) => this.#isLinkedAddonLine(item, config.type));
        let quantityToRemove = Math.abs(quantityDelta);
        /** @type {Record<string, number>} */
        const updates = {};

        for (const addonLine of addonLines) {
          if (quantityToRemove <= 0) break;

          const currentQuantity = Number(addonLine?.quantity) || 0;
          if (currentQuantity <= 0) continue;

          const removeNow = Math.min(currentQuantity, quantityToRemove);
          const nextQuantity = currentQuantity - removeNow;
          quantityToRemove -= removeNow;

          const lineKey = `${addonLine?.key || ''}`.trim();
          if (lineKey) {
            updates[lineKey] = nextQuantity;
          }
        }

        if (Object.keys(updates).length === 0) continue;

        const body = JSON.stringify({
          updates,
          sections,
          sections_url: window.location.pathname,
        });

        const updateResponse = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
        const updateText = await updateResponse.text();
        const updateData = JSON.parse(updateText);
        if (updateData?.errors) continue;

        cart = updateData;
        this.dispatchEvent(
          new CartUpdateEvent(updateData, this.sectionId, {
            source: 'linked-addon-cart-guard',
            sections: updateData?.sections,
          })
        );
      }
    } catch (error) {
      console.error('Failed to auto-sync linked add-ons:', error);
    } finally {
      linkedAddonGuardSyncInFlight = false;
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
